/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-misused-promises */
import {useEffect, useState} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {FileUploader} from 'react-drag-drop-files';
import {Controller, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import {Button, Form} from 'react-bootstrap';

import {usePostUpload} from 'hooks';
import {API_BASE_URL} from 'config';

type TFormData = {
	title: string;
	description: string;
	file?: File;
};

type TProps = {
	type: 'ReMap' | 'Policy';
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	isEditTitle?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ModalUploadFile = ({
	type,
	open,
	onClose,
	refetch,
	isEditTitle = '',
}: TProps) => {
	const {fetchUpload} = usePostUpload();

	const [file, setFile] = useState<File>();
	const {
		control,
		handleSubmit,
		formState: {errors, isValid},
		setValue,
	} = useForm<TFormData>({
		mode: 'all',
	});

	useEffect(() => {
		setValue('title', isEditTitle || '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditTitle]);

	/**
	 * This function sets the value of a file input to a given argument.
	 * @param {File} arg - The parameter `arg` is of type `File`, which is a built-in interface in
	 * TypeScript that represents a file from the user's file system. In this case, the `handleChange`
	 * function takes a `File` as an argument and sets it as the state of a component using the `set
	 */
	const handleChange = (arg: File) => setFile(arg);

	/**
	 * This function displays an error message using the toast library, indicating whether the file type
	 * is CSV or XML.
	 */
	const handleError = () =>
		toast.error(`${type === 'ReMap' ? 'CSV' : 'XML'} file error`);

	/**
	 * A function that is called when the form is submitted.
	 * @param {TFormData} data - TFormData
	 */
	const onSubmit = async (data: TFormData) => {
		try {
			const formData = new FormData();
			formData.append('file', file as never);
			formData.append('title', data.title);
			formData.append('description', data.description);

			const path =
				type === 'ReMap'
					? '/remap-mgmt/v1/re-map-set'
					: '/policy-engine-mgmt/v1/policy-set';
			await fetchUpload(`${API_BASE_URL}${path}`, formData);
			toast.success('Created Successfully!');
			// refetch data
			refetch();

			setFile(undefined);
			setValue('title', '');
			// close modal
			onClose();
		} catch (error: any) {
			toast.error(error.response.data);
		}
	};

	const titleReMap = isEditTitle
		? 'Mappings and Override Transformations'
		: 'Mappings Transformations';

	return (
		<Modal
			centered
			size="xl"
			show={open}
			onHide={() => onClose()}
			className="custom-modal-style">
			<Modal.Header closeButton>
				<Modal.Title id="contained-modal-title-vcenter">
					<h4 className="modal-title" id="modalUploadFileLabel">
						<i className="bi bi-upload text-dark mr-2" /> Upload{' '}
						{type === 'ReMap' ? titleReMap : 'Policy'}
					</h4>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="show-grid">
				<form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
					<div className="row">
						<div className="col-md-12 mb-4">
							<label className="font-weight-bold text-dark">Title</label>

							<Controller
								name="title"
								control={control}
								rules={{
									required: true,
									maxLength: 100,
								}}
								render={({field: {onChange, onBlur, value}}) => (
									<Form.Control
										disabled={isEditTitle !== ''}
										onChange={val => onChange(val) as never}
										onBlur={() => onBlur()}
										type="text"
										className="form-control"
										id="title"
										value={value}
										placeholder="Title"
									/>
								)}
							/>
							{errors.title && errors.title.type === 'required' && (
								<div className="error-input">Valid Title name is required.</div>
							)}
						</div>
						<div className="col-md-12 mb-4">
							<label className="text-dark">Description</label>
							<Controller
								name="description"
								control={control}
								rules={{
									required: true,
									maxLength: 200,
								}}
								render={({field: {onChange, onBlur, value}}) => (
									<Form.Control
										disabled={isEditTitle !== ''}
										onChange={val => onChange(val) as never}
										onBlur={() => onBlur()}
										type="text"
										className="form-control"
										id="description"
										value={value}
										placeholder="Description"
									/>
								)}
							/>
							{errors.title && errors.title.type === 'required' && (
								<div className="error-input">
									Valid Description is required.
								</div>
							)}
						</div>
						<div className="col-md-12">
							<FileUploader
								handleChange={handleChange}
								name="file"
								types={type === 'ReMap' ? ['CSV'] : ['XML']}
								required={file ? false : true}
								onTypeError={handleError}
								classes="mw-100 upload-file"
							/>
						</div>
					</div>

					<div className="d-flex justify-content-between mt-5">
						<Button variant="secondary" onClick={() => onClose()}>
							<i className="bi bi-x  mr-2" />
							Close
						</Button>

						<Button
							type="submit"
							variant="primary"
							disabled={!isValid || file === undefined}>
							<i className="bi bi-check  mr-2" /> Upload
						</Button>
					</div>
				</form>
			</Modal.Body>
		</Modal>
	);
};

export default ModalUploadFile;
